import { useCallback, useEffect, useMemo, useState } from 'react';
import { lerRateio, useVersaoDoRateio } from './useRateio';
import { RateioDoGrupo } from '../utils/rateioDoGrupo';
import { montarPainelDoGrupo, PainelDoGrupo } from '../utils/quemUsaQuemPaga';
import { marcaDoGrupoSolto, MarcaDoGrupoSolto, matrizesCandidatas, temMolduraDeGrupo } from '../utils/grupoNaLista';
import { useEstadoDaCarga, lerEmLote } from '../design';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';

/**
 * Os grupos econômicos com rateio que aparecem numa lista, e de qual grupo é
 * cada empresa. Um hook só para Assinaturas e Faturas: mesma consulta, mesmo
 * cálculo (`montarPainelDoGrupo`, o da ficha e de Grupos Econômicos).
 *
 * Só consulta o rateio dos grupos com 2+ empresas com assinatura (em 10/2026,
 * PARANAFRIGOR e CONCEITO).
 *
 * FALHA DE LEITURA NÃO É EMPRESA AVULSA (07/10/2026). Antes, a matriz cuja leitura falhava sumia do mapa
 * (`.catch(() => null)` + `if (par)`), e as empresas dela apareciam como avulsas, sem a linha do grupo que soma e sem
 * "do grupo · X% do compartilhado": a estrutura societária errada, em tela de rateio, sem aviso nenhum. Agora a
 * leitura é um lote (design/cargaDaLista → lerEmLote) e o que faltou sai em `faltaram`, para a tela avisar
 * (components/AvisoDosGruposEconomicos). A matriz que falha numa RELEITURA mantém a última leitura boa: erro não zera
 * os dados que já estavam na tela.
 */
export function useGruposEconomicos(clients: any[], contracts: any[]) {
  const [rateios, setRateios] = useState<Record<number, RateioDoGrupo>>({});
  const candidatas = useMemo(() => matrizesCandidatas(clients || [], contracts || []).join(','), [clients, contracts]);
  // Assinatura gravada esquece o rateio: lê de novo (ramais do painel).
  const versao = useVersaoDoRateio();
  const [tentativa, setTentativa] = useState(0);
  const tentarDeNovo = useCallback(() => setTentativa((n) => n + 1), []);
  const { rodar, ...carga } = useEstadoDaCarga();
  const [idsQueFaltaram, setIdsQueFaltaram] = useState<number[]>([]);

  const nomeDaMatriz = useCallback(
    (id: number) => {
      const c = (clients || []).find((x: any) => x.id === id);
      return nomeDeExibicao(c, `cliente ${id}`);
    },
    [clients],
  );

  useEffect(() => {
    if (!candidatas) return;
    let vivo = true;
    const ids = candidatas.split(',').map(Number);
    rodar(async () => {
      const { lidos, resultado } = await lerEmLote(ids, (id) => lerRateio(id) as Promise<RateioDoGrupo>, nomeDaMatriz);
      if (!vivo) return resultado;
      const lidosIds = new Set(lidos.map((l) => l.item));
      setIdsQueFaltaram(ids.filter((id) => !lidosIds.has(id)));
      setRateios((antes) => {
        const m: Record<number, RateioDoGrupo> = {};
        for (const { item, valor } of lidos) m[item] = valor;
        // A matriz que falhou agora e tinha leitura boa antes segue com ela.
        for (const id of ids) if (!(id in m) && antes[id]) m[id] = antes[id];
        return m;
      });
      return resultado;
    });
    return () => { vivo = false; };
    // nomeDaMatriz muda com `clients`, que já muda `candidatas`: não relê por isso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatas, versao, tentativa, rodar]);

  const paineis = useMemo(() => {
    const m = new Map<number, PainelDoGrupo>();
    for (const [id, r] of Object.entries(rateios)) {
      const p = montarPainelDoGrupo(r, contracts || [], clients || []);
      if (p) m.set(Number(id), p);
    }
    return m;
  }, [rateios, contracts, clients]);

  // G1 (03/11/2026): só grupo com 2+ empresas no rateio vigente vira moldura. O de uma empresa só sai de
  // `matrizDoMembro` (as duas telas agrupam por ele) e vai para `grupoSolto`, que dá a marca da linha.
  const matrizDoMembro = useMemo(() => {
    const m = new Map<number, number>();
    paineis.forEach((p, matriz) => { if (temMolduraDeGrupo(p)) p.empresas.forEach((e) => m.set(e.clientId, matriz)); });
    return m;
  }, [paineis]);

  const grupoSolto = useMemo(() => {
    const m = new Map<number, MarcaDoGrupoSolto>();
    paineis.forEach((p, matriz) => {
      if (temMolduraDeGrupo(p)) return;
      const marca = marcaDoGrupoSolto(matriz, nomeDaMatriz(matriz), clients || [], contracts || []);
      p.empresas.forEach((e) => m.set(e.clientId, marca));
    });
    return m;
  }, [paineis, clients, contracts, nomeDaMatriz]);

  // O que a tela precisa dizer: matrizes que não carregaram, e se a tela segue mostrando o grupo (leitura anterior)
  // ou se as empresas dele estão aparecendo como avulsas.
  const gruposQueFaltaram = idsQueFaltaram.map((id) => ({ nome: nomeDaMatriz(id), avulsas: !rateios[id] }));

  return { paineis, matrizDoMembro, grupoSolto, gruposQueFaltaram, erroDosGrupos: carga.erro, tentarDeNovo };
}
